import { useNavigate } from "react-router-dom";
import {
  X,
  Trash2,
  Minus,
  Plus,
  ShoppingCart,
  User,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCartStore, CartItem } from "@/stores/cartStore";

import { cn } from "@/lib/utils";
import { toast } from "@/hooks/useToast";
import { useState } from "react";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const navigate = useNavigate();
  const {
    items,
    getTotalPrice,
    isAnonymous,
    removeItemFromBackend,
    updateQuantityInBackend,
    clearCart,
    clearCartOptimistic,
    updateItemQuantityOptimisticWithFallback,
    removeItemOptimisticWithFallback,
    loading,
  } = useCartStore();
  


  const [isProcessing, setIsProcessing] = useState(false);

  // Debug: Log items when they change
  // console.log("🔍 Current items in cart:", items);

  const handleCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  const handleQuantityChange = async (item: CartItem, newQuantity: number) => {
    console.log("🎯 handleQuantityChange called:", { item, newQuantity });
    try {
      setIsProcessing(true);

      if (newQuantity <= 0) {
        // Si la cantidad llega a 0, eliminar el item optimistamente
        const identifier = item.cartItemId || item.id;
        console.log("🗑️ Removing item with identifier:", identifier);
        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        removeItemOptimisticWithFallback(identifier);

        // Procesar en background
        if (item.cartItemId) {
          await removeItemFromBackend(item.cartItemId);
        }
      } else {
        // Actualizar cantidad optimistamente
        const identifier = item.cartItemId || item.id;
        console.log("item.cartItemId", item.cartItemId);
        console.log("item.id", item.id);
        console.log(
          "📝 Updating quantity for identifier:",
          identifier,
          "to:",
          newQuantity
        );
        // 🚀 OPTIMISTIC UPDATE: Actualizar UI inmediatamente
        updateItemQuantityOptimisticWithFallback(identifier, newQuantity);

        // Procesar en background
        if (item.cartItemId) {
          console.log("🔄 Actualizando cantidad en el backend");
          await updateQuantityInBackend(item.cartItemId, newQuantity);
        }
      }
    } catch (error) {
      console.error("Error actualizando cantidad:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la cantidad del producto",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveItem = async (item: CartItem) => {
    console.log("🎯 handleRemoveItem called:", { item });
    try {
      setIsProcessing(true);

      const identifier = item.cartItemId || item.id;
      console.log("🗑️ Removing item with identifier:", identifier);
      // 🚀 OPTIMISTIC UPDATE: Eliminar inmediatamente de la UI
      removeItemOptimisticWithFallback(identifier);

      // Procesar en background
      if (item.cartItemId) {
        await removeItemFromBackend(item.cartItemId);
      }
    } catch (error) {
      console.error("Error eliminando item:", error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el producto del carrito",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearCart = async () => {
    try {
      setIsProcessing(true);

      // 🚀 OPTIMISTIC UPDATE: Limpiar inmediatamente
      clearCartOptimistic();

      // Procesar en background
      await clearCart();
    } catch (error) {
      console.error("Error limpiando carrito:", error);
      toast({
        title: "Error",
        description: "No se pudo limpiar el carrito",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-full max-w-md bg-background border-l border-border",
          "transform transition-transform duration-300 ease-in-out z-50",
          "flex flex-col shadow-xl",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-card-foreground">
              Carrito de Compras
            </h2>
            {items.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {items.length}
              </Badge>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* User Status Alert */}
        {isAnonymous && (
          <Alert className="m-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="text-sm">
                  Comprando como invitado. Se te pedirán tus datos al finalizar.
                </span>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                Tu carrito está vacío
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Agrega productos para comenzar a comprar
              </p>
              <Button onClick={onClose} variant="outline">
                Continuar Comprando
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-4 p-4 border border-border rounded-lg bg-card"
                >
                  {/* Product Image */}
                  <div className="flex-shrink-0">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                        <ShoppingCart className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-card-foreground truncate">
                      {item.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      ${item.price.toLocaleString()}
                    </p>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        handleQuantityChange(item, item.quantity - 1)
                      }
                      className="h-8 w-8"
                      disabled={isProcessing}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-8 text-center text-sm font-medium">
                      {loading ? (
                        <div className="inline-flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : (
                        item.quantity
                      )}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() =>
                        handleQuantityChange(item, item.quantity + 1)
                      }
                      className="h-8 w-8"
                      disabled={isProcessing}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveItem(item)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    disabled={isProcessing}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-border p-4 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-card-foreground">
                Total:
              </span>
              <span className="text-lg font-bold text-primary">
                ${getTotalPrice().toLocaleString()}
              </span>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleClearCart}
                className="flex-1"
                disabled={isProcessing}
              >
                {isProcessing ? "Procesando..." : "Vaciar Carrito"}
              </Button>
              <Button
                onClick={handleCheckout}
                className="flex-1"
                disabled={isProcessing}
              >
                {isAnonymous ? "Continuar como Invitado" : "Finalizar Compra"}
              </Button>
            </div>

            {isAnonymous && (
              <p className="text-xs text-muted-foreground text-center">
                Al continuar, se te pedirán tus datos de contacto para procesar
                el pedido
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
